import { Instagram, Facebook, Linkedin, Youtube, MapPin, Phone, Mail } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { site } from "@/data/site";

export const Footer = () => {
    const year = new Date().getFullYear();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const go = (href) => {
        if (pathname === "/") {
            document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
        } else {
            navigate(`/${href}`);
        }
    };

    const explore = [
        { label: "Signature Collection", href: "#signature" },
        { label: "Developers", href: "#developers" },
        { label: "Locations", href: "#locations" },
        { label: "Mumbai Property Files", href: "#files" },
    ];

    return (
        <footer data-testid="footer" className="border-t border-line bg-sand px-6 pt-20 lg:px-10">
            <div className="mx-auto max-w-[1400px]">
                <div className="grid gap-12 pb-16 md:grid-cols-2 lg:grid-cols-4">
                    <div className="md:col-span-2 lg:col-span-1">
                        <div className="flex items-center gap-3">
                            <img src={site.logo} alt="Swastik Realty" className="h-14 w-14 object-contain" />
                            <span className="font-serif text-xl text-charcoal">Swastik Realty</span>
                        </div>
                        <p className="mt-4 text-sm uppercase tracking-luxe text-orange">Find | Trust | Grow</p>
                        <p className="mt-5 max-w-xs text-sm leading-relaxed text-slatey">
                            A premium, RERA-registered luxury real estate advisory in Mumbai — helping
                            successful people make one of life's most important decisions.
                        </p>
                        <div className="mt-7 flex items-center gap-3">
                            {[
                                { Icon: Instagram, href: site.social.instagram, label: "Instagram" },
                                { Icon: Facebook, href: site.social.facebook, label: "Facebook" },
                                { Icon: Linkedin, href: site.social.linkedin, label: "LinkedIn" },
                                { Icon: Youtube, href: site.social.youtube, label: "YouTube" },
                                { Icon: MapPin, href: site.social.google, label: "Google" },
                            ]
                                .filter((s) => s.href)
                                .map(({ Icon, href, label }) => (
                                <a
                                    key={label}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={label}
                                    data-testid={`social-${label.toLowerCase()}`}
                                    className="flex h-10 w-10 items-center justify-center border border-line text-charcoal transition-colors duration-300 hover:border-orange hover:bg-orange hover:text-white"
                                >
                                    <Icon size={17} />
                                </a>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className="text-[10px] uppercase tracking-luxe text-slatey">Explore</p>
                        <ul className="mt-6 space-y-4">
                            {explore.map((it) => (
                                <li key={it.label}>
                                    <button
                                        onClick={() => go(it.href)}
                                        className="text-sm text-charcoal/80 transition-colors duration-300 hover:text-orange"
                                    >
                                        {it.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className="text-[10px] uppercase tracking-luxe text-slatey">Contact</p>
                        <ul className="mt-6 space-y-4 text-sm text-charcoal/80">
                            <li>
                                <a href={`tel:${site.contact.phone}`} className="flex items-center gap-3 transition-colors duration-300 hover:text-orange">
                                    <Phone size={15} className="text-orange" />
                                    {site.contact.phoneDisplay}
                                </a>
                            </li>
                            <li>
                                <a href={`mailto:${site.contact.email}`} className="flex items-center gap-3 transition-colors duration-300 hover:text-orange">
                                    <Mail size={15} className="text-orange" />
                                    {site.contact.email}
                                </a>
                            </li>
                            <li className="text-slatey">{site.contact.address}</li>
                        </ul>
                    </div>

                    <div>
                        <p className="text-[10px] uppercase tracking-luxe text-slatey">Compliance</p>
                        <p className="mt-6 text-sm text-charcoal/80">MahaRERA Registered</p>
                        <p className="mt-2 font-mono text-xs tracking-wider text-orange">{site.rera}</p>
                        <p className="mt-4 text-xs leading-relaxed text-slatey">
                            Swastik Realty acts as an authorised channel partner. All transactions are
                            governed by respective developer RERA registrations.
                        </p>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-between gap-3 border-t border-line py-8 sm:flex-row">
                    <p className="text-xs text-slatey">© {year} Swastik Realty. All rights reserved.</p>
                    <p className="text-[10px] uppercase tracking-luxe text-slatey/70">
                        Luxury Real Estate Advisory · Mumbai
                    </p>
                </div>
            </div>
        </footer>
    );
};
